import { IUnifiedRolesDomain } from '../../../UnifiedRoles';
import { TenantSpecificTenantAdminRolesEnum } from '../../../UnifiedRoles/domain/enums';
import {
  IUserUnifiedRolesTenantSpecificAggregate,
  IUserUnifiedRolesTenantSpecificAggregateConstructor,
} from '../interfaces';

export class UserUnifiedRolesTenantSpecificAggregate
  implements IUserUnifiedRolesTenantSpecificAggregate
{
  public unifiedRolesEditorFallback: IUnifiedRolesDomain.IUnifiedRolesEditorPolicy;

  private constructor(
    private readonly payload: IUserUnifiedRolesTenantSpecificAggregateConstructor,
  ) {
    this.unifiedRolesEditorFallback =
      this.payload.createUnifiedRolesEditorPolicy({});
  }

  public readonly getUserTenantSpecificUnifiedRoles =
    async (): Promise<IUnifiedRolesDomain.IUnifiedRolesEditorPolicy> => {
      const { userTenantSpecificUnifiedRoles, tenantUnifiedRoles } =
        await this.composeUserTenantSpecificUnifiedRolesPayload();

      return this.payload.createUnifiedRolesEditorPolicy({
        unifiedRolesAssignedToResource: userTenantSpecificUnifiedRoles,
        allAvailableUnifiedRoles: tenantUnifiedRoles,
      });
    };

  public readonly updateUserTenantSpecificUnifiedRoles = async (
    updatedUserAssignedUnifiedRoles: IUnifiedRolesDomain.TUnifiedRoleCollectionDto,
  ): Promise<void> => {
    await this.payload.userTenantSpecificUnifiedRolesUpdateCommand.execute(
      this.payload.userPrincipal,
      {
        tenant: this.payload.tenant,
        user: this.payload.user,
        unifiedRoles: updatedUserAssignedUnifiedRoles,
      },
    );
  };

  public readonly changeUserTenantSpecificAdminRolesType = async (
    tenantSpecificTenantAdminPolicy: TenantSpecificTenantAdminRolesEnum,
  ): Promise<IUnifiedRolesDomain.TUnifiedRolesMap> => {
    await this.payload.changeUserTenantSpecificAdminRolesType.execute(
      this.payload.userPrincipal,
      {
        userTenantSpecificRolesHandler: this,
        tenantSpecificTenantAdminPolicy,
      },
    );

    const userTenantSpecificRolesPolicy =
      await this.getUserTenantSpecificUnifiedRoles();

    return userTenantSpecificRolesPolicy.composeResourceTenantSpecificRolesMap();
  };

  public static create(
    payload: IUserUnifiedRolesTenantSpecificAggregateConstructor,
  ): UserUnifiedRolesTenantSpecificAggregate {
    return new UserUnifiedRolesTenantSpecificAggregate(payload);
  }

  private readonly composeUserTenantSpecificUnifiedRolesPayload =
    async (): Promise<{
      userTenantSpecificUnifiedRoles: IUnifiedRolesDomain.TUnifiedRoleCollectionDto;
      tenantUnifiedRoles: IUnifiedRolesDomain.TUnifiedRoleCollectionDto;
    }> => {
      const [{ userTenantSpecificUnifiedRoles }, { tenantUnifiedRoles }] =
        await Promise.all([
          this.payload.userTenantSpecificUnifiedRolesQuery.execute(
            this.payload.userPrincipal,
            {
              tenant: this.payload.tenant,
              user: this.payload.user,
            },
          ),
          this.payload.tenantUnifiedRolesQuery.execute(
            this.payload.userPrincipal,
            {
              tenant: this.payload.tenant,
            },
          ),
        ]);

      return {
        userTenantSpecificUnifiedRoles,
        tenantUnifiedRoles,
      };
    };
}
